import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy } from '@core/classes/untilDestroy.class';
import { MenuAction, MenuDesktopConfirmComponent, MenuMobileComponent } from '@core/components/menu/menu.component';
import { ACTION } from '@core/components/payment-method-list/payment-method-list.component';
import { NOTICE_TYPE } from '@core/constants';
import { FI_ALREADY_LINKED_CODE } from '@core/constants/error-code';
import { QUERY_PARAMS_KEYS } from '@core/constants/query-params';
import { isMobile, isCheckout } from '@core/utils';
import { ToastService } from '@core/services/toast.service';
import { LinkBankService } from '@core/services/link-bank.service';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ILinkedFI, IUserProfile } from '@store/common/user.interface';
import * as CoreActions from '@store/core/core.actions';
import { LoaderState, FinancialInstitution } from '@store/core/core.interface';
import { selectOIDCCode, selectOIDCCodeDetail, selectFinancialInstitutions } from '@store/core/core.selectors';
import { UserActions } from '@store/user/user.actions';
import { selectLinkedFIIds, selectUserProfile, selectUserProfileRef } from '@store/user/user.selectors';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, Observable, takeUntil, tap, withLatestFrom, combineLatest, map, take } from 'rxjs';
import { PaymentMethodListComponent } from '../../../../core/components/payment-method-list/payment-method-list.component';

import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PbbSvgIconComponent } from '@core/components/pbb-svg-icon/pbb-svg-icon.component';
import { selectFI } from '@store/core/core.actions';
import { LetDirective } from '@ngrx/component';
import { updateConsentGrant } from '@store/checkout/checkout.actions';
import { selectConsentGrantPayload } from '@store/checkout/checkout.selectors';
import {PbbNoticeComponent} from '@core/components/pbb-notice/pbb-notice.component';
import {PbbSpinnerComponent} from '@core/components';
import {WindowService} from '@core/services/window.service';
@Component({
  selector: 'pbb-manage-payment-methods-page',
  templateUrl: './manage-payment-methods-page.component.html',
  styleUrls: ['./manage-payment-methods-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,
    NgxSkeletonLoaderModule,
    PaymentMethodListComponent,
    PbbNoticeComponent,
    PbbSvgIconComponent,
    TranslateModule,
    MatIconModule,
    RouterModule,
    FormsModule,
    PbbSpinnerComponent
  ],
})
export class ManagePaymentMethodsPageComponent extends UntilDestroy implements OnInit {
  selectedAccount: string | null = null;
  defaultSelectedAccount: string | null = null;
  refreshTokenExpired: boolean = false;
  userProfile$: Observable<LoaderState<IUserProfile> | undefined> = this.store.select(selectUserProfile).pipe(
    takeUntil(this.destroy$),
    filter((userProfile) => !!userProfile),
    tap((userProfile) => {
      this.isLastFi = userProfile?.data?.linked_fi_info.length === 1;
      this.refreshTokenExpired = userProfile?.data?.linked_fi_info.some(fi => fi.refresh_token_expired) ?? false;
      if (userProfile?.data) {
        this.setDefaultSelection(userProfile.data);
      }
    })
  );
  noticeType = NOTICE_TYPE;
  isLastFi = false;
  linkedFI?: ILinkedFI;
  isEmbeddedView: boolean = false;
  isMobile = isMobile();
  isCheckout = isCheckout();
  oidcDetails$ = this.store.select(selectOIDCCodeDetail('checkout'));
  fiAlreadyLinkedCode = FI_ALREADY_LINKED_CODE;
  errorKey!: string;
  private readonly financialInstitutions$ = this.store.select(selectFinancialInstitutions);
  private readonly userProfileRef$ = this.store.select(selectUserProfileRef);

  constructor(
    private readonly store: Store,
    private readonly _bottomSheet: MatBottomSheet,
    public dialog: MatDialog,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService,
    private readonly linkBankService: LinkBankService,
    private readonly router: Router,
    private windowService: WindowService
  ) {
    super();
  }

  ngOnInit(): void {
    this.oidcDetails$
      .pipe(takeUntil(this.destroy$), withLatestFrom(this.store.select(selectOIDCCode)))
      .subscribe(([oidcDetail, oidcCode]) => {
        if (oidcDetail && oidcCode === this.fiAlreadyLinkedCode) {
          this.errorKey = oidcDetail.noticeTranslateKey;
          this.isMobile ? this.openMobileMenu() : this.openDesktopMenu();
        }
      });

    this.linkBankService.handleFILinkSuccess(this.destroy$);

    if (this.isCheckout) {
      this.store
        .select(selectConsentGrantPayload)
        .pipe(
          takeUntil(this.destroy$),
          filter((consentGrant) => !!consentGrant?.fi_account_ref)
        )
        .subscribe((consentGrant) => {
          this.selectedAccount = this.createAccountSelection(
            consentGrant.fi_account_ref!,
            consentGrant.selected_fi_id!,
            consentGrant.fi_user_id!
          );
        });
    }
    this.isEmbeddedView = this.windowService.isEmbedded();
  }

  setDefaultSelection(userProfile: IUserProfile) {
    if (this.isCheckout) return;

    const defaultRef = userProfile?.default_fi_account_ref;
    const found = this.findAccountInProfile(userProfile, defaultRef);

    this.selectedAccount = found;
    this.defaultSelectedAccount = found;
  }

  private createAccountSelection(accountId: string, fiId: string, fiUserId: string): string {
    return JSON.stringify({
      selectedAccountId: accountId,
      selectedFiId: fiId,
      selectedFiUserId: fiUserId,
    });
  }

  private findAccountInProfile(userProfile: IUserProfile, accountRef?: string): string | null {
    if (!accountRef) return null;

    const linkedFis = userProfile?.linked_fi_info || [];

    for (const fi of linkedFis) {
      const account = fi.accounts?.find((acc) => acc.fi_account_ref === accountRef);
      if (account && fi.linked_fi_id && fi.fi_user_id) {
        return this.createAccountSelection(account.fi_account_ref, fi.linked_fi_id, fi.fi_user_id);
      }
    }

    return null;
  }

  openLinkBankSheet(event?: Event): void {
    this.linkBankService.openLinkBankSheet(this.userProfile$, window.location.pathname, event);
  }

  fiSelected(selectedFI: FinancialInstitution, userProfileRef?: string) {
    const selectFIPayload: {
      selectedFI: FinancialInstitution;
      redirectUrl: string;
      userProfileRef?: string;
      rus?: boolean;
    } = {
      selectedFI,
      redirectUrl: '/user-portal/registered/managePaymentMethods',
      rus: false,
    };

    if (userProfileRef) {
      selectFIPayload.userProfileRef = userProfileRef;
    }

    this.store.dispatch(selectFI(selectFIPayload));
  }

  setMenuAction(menuAction: MenuAction) {
    switch (menuAction.action) {
      case ACTION.UNLINK:
        this.linkedFI = menuAction.item;
        this.store.dispatch(
          UserActions.unlinkFinancialInstituteStart({
            linkedFiRef: menuAction?.item?.linked_fi_id || '',
            fiUserId: menuAction?.item?.fi_user_id,
          })
        );
        break;
      case ACTION.UNLINK_LAST:
        this.linkedFI = menuAction.item;
        this.store.dispatch(
          UserActions.unlinkFinancialInstituteStart({
            linkedFiRef: menuAction?.item?.linked_fi_id || '',
            fiUserId: menuAction?.item?.fi_user_id,
            signout: true,
          })
        );
        break;
      case ACTION.DEFAULT:
        if (this.isCheckout) {
          this.store.dispatch(
            updateConsentGrant({
              consentGrantPayload: {
                fi_account_ref: menuAction?.item?.paymentMethodRef || '',
                selected_fi_id: menuAction?.item?.linkedFiRef || '',
                fi_user_id: menuAction?.item?.fi_user_id || '',
              },
            })
          );
        } else {
          this.store.dispatch(
            UserActions.setDefaultPaymentMethodStart({
              linkedFiRef: menuAction?.item?.linkedFiRef,
              paymentMethodRef: menuAction?.item?.paymentMethodRef || '',
            })
          );
        }
        break;
    }
  }

  onContinue(): void {
    if (this.isCheckout) {
      this.router.navigate(['/checkout/registered/orderReview']);
    }
  }

  openMobileMenu(): void {
    const btmSheetRef = this._bottomSheet.open(MenuMobileComponent, {
      data: {
        options: [
          {
            action: 'COMMON.BUTTON.LINK_ANOTHER_BANK',
            confirmTitle: 'CHECKOUT.LINK_BANK.FAILURE_TITLE',
            confirmSubtitle: 'CHECKOUT.LINK_BANK.FAILURE_SUBTITLE',
            confirmCancel: 'COMMON.BUTTON.CLOSE',
          },
        ],
        showConfirm: true,
        isErrorInline: false,
        errorKey: this.errorKey,
        currentDisplay: 'COMMON.BUTTON.LINK_ANOTHER_BANK',
      },
      panelClass: 'content_main-drawer',
      backdropClass: 'content_main-bottom-sheet',
    });
    btmSheetRef
      .afterDismissed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(
          CoreActions.setOIDCCode({
            OIDCCode: undefined,
          })
        );
      });
  }

  openDesktopMenu(): void {
    const dialogRef = this.dialog.open(MenuDesktopConfirmComponent, {
      data: {
        option: {
          action: 'COMMON.BUTTON.LINK_ANOTHER_BANK',
          confirmTitle: 'CHECKOUT.LINK_BANK.FAILURE_TITLE',
          confirmSubtitle: 'CHECKOUT.LINK_BANK.FAILURE_SUBTITLE',
          confirmCancel: 'COMMON.BUTTON.CLOSE',
        },
        isErrorInline: false,
        errorKey: this.errorKey,
      },
      closeOnNavigation: true,
      autoFocus: false,
      width: '416px',
      panelClass: 'content_overlay-medium',
      backdropClass: 'content_overlay-dialog',
    });
    dialogRef.afterClosed().subscribe(() => {
      this.store.dispatch(
        CoreActions.setOIDCCode({
          OIDCCode: undefined,
        })
      );
    });
  }
}
